import { nanoid } from "nanoid";
import { AnnotationResizerLocation } from "@syncfusion/ej2-pdfviewer";
import AnnotationType from "../../models/annotationType.js";

export function createTaskStampImage(color, initials, task) {
	const canvas = document.createElement(`canvas`);
	const ctx = canvas.getContext(`2d`);

	const scale = 2;
	const radius = 17.5 * scale;
	const x = Math.ceil(radius + scale);
	const y = Math.ceil(radius + scale);
	const taskFont = `${14 * scale}px sans-serif`;
	const Phi = 1.681;
	const taskPadding = Math.round(radius / Phi);

	ctx.font = taskFont;
	let text = ctx.measureText(task);
	let width = Math.ceil(text.width + radius * 3 + taskPadding);
	let height = Math.ceil(y + radius + scale);

	canvas.width = width;
	canvas.height = height;

	const circle = new Path2D();
	circle.arc(x, y, radius, 0, 2 * Math.PI);

	const pill = new Path2D();
	pill.arc(x, y, radius, -Math.PI / 2, Math.PI / 2);
	pill.arc(width - radius - scale, y, radius, Math.PI / 2, -Math.PI / 2, true);
	pill.closePath();

	ctx.lineWidth = scale;
	ctx.strokeStyle = `#CCCCCC`;

	ctx.fillStyle = color;
	ctx.fill(circle);
	ctx.stroke(circle);

	ctx.fillStyle = `#F2F2F2`;
	ctx.fill(pill);
	ctx.stroke(pill);

	ctx.fillStyle = color;
	ctx.font = taskFont;
	ctx.fillText(
		task,
		x + radius + taskPadding,
		y + text.actualBoundingBoxAscent / 2
	);

	ctx.fillStyle = `#F2F2F2`;
	ctx.font = `${12 * scale}px sans-serif`;
	text = ctx.measureText(initials);
	ctx.fillText(
		initials,
		x - text.width / 2,
		y + text.actualBoundingBoxAscent / 2
	);

	width /= scale;
	height /= scale;
	return { width, height, image: canvas.toDataURL(`image/png`) };
}

async function BlobToDataURL(blob) {
	const reader = new FileReader();
	reader.readAsDataURL(blob);
	return await new Promise(resolve => reader.addEventListener(`load`, () => resolve(reader.result)));
}

async function URLtoDataURL(url) {
	return await BlobToDataURL(await (await window.fetch(url)).blob());
}

async function getWidthAndHeight(dataURL) {
	const img = new Image();
	img.src = dataURL;
	return await new Promise(resolve => img.addEventListener(`load`, () => resolve({ width: img.width, height: img.height })));
}

export async function BlobToImage(blob) {
	const data = await BlobToDataURL(blob);
	return { data, ...await getWidthAndHeight(data) };
}

export async function URLtoImage(url) {
	const data = await URLtoDataURL(url);
	return { data, ...await getWidthAndHeight(data) };
}

export async function ImageWithDimensions(data) {
	return { data, ...await getWidthAndHeight(data) };
}


export function textMeasure(text, fontSize, fontFamily) {
	const canvas = document.querySelector(`#pdfviewer_pageViewContainer canvas`);
	const ctx = canvas.getContext(`2d`);
	const oldFont = ctx.font;
	ctx.font = `${fontSize}px ${fontFamily}`;
	const textMeasure = ctx.measureText(`${text} `);
	let height = textMeasure.fontBoundingBoxAscent + textMeasure.fontBoundingBoxDescent;
	if(!height) {
		const heightMeasure  = ctx.measureText(`ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz`);
		height = heightMeasure.actualBoundingBoxAscent + heightMeasure.actualBoundingBoxDescent;
	}
	let width = Math.max(Math.ceil(textMeasure.width), Math.ceil(textMeasure.actualBoundingBoxRight + Math.abs(textMeasure.actualBoundingBoxLeft))) + 10;
	ctx.font = oldFont;
	return {
		width,
		height
	};
}


// Annotation border color on move is hardcoded in MoveTool.prototype.mouseMove:
//   free text is blue, custom stamp has none, others (ex. signature) draw red shadow instead of border
export const selectColor = `#4285F4`;
export const selectorLineDashArray = [1, 0];
export const resizerShape = `Circle`;
export const resizerLocation = AnnotationResizerLocation.Corners;

export const textAllowedInteractions = [`Select`, `Move`, `Delete`];
export const fontFamily = `Open Sans`;
export const textFontColor = `#000000`;
export const customTextFontColor = `#CCCCCC`;
export const customTextContent = `Type here`;
export const defaultFontSize = 16;
export const defaultSignatureFontSize = 32;
export const TextAlign = {
	Left: `Left`,
	Center: `Center`,
};

export const dateToString = (date) => date.toLocaleDateString(`en-US`, { dateStyle: `long` });

const date = new Date();
const day = date.getDate();
const month = date.toLocaleDateString(`en-US`, { month: `long` });
const year = date.toLocaleDateString(`en-US`, { year: `numeric` });

const nthNumber = (number) => {
	if (number > 3 && number < 21) return `th`;
	const remainder = number % 10;
	if (remainder === 1) return `st`;
	if (remainder === 2) return `nd`;
	if (remainder === 3) return `rd`;
	return `th`;
};
export const todaysDay = () => `${day}${nthNumber(day)}`;
export const todaysMonth = () => `${month}`;
export const todaysYear = () => `${year}`;
export const todaysDate = () => `${month} ${day}${nthNumber(day)}, ${year}`;


class CompletedTaskCustomData {
	isTask = true;
	isComplete = true;
}

class AnnotationSettings {
	constructor(isLock) {
		this.isLock = isLock;
	}
	minWidth = 0;
	maxWidth = 2000;
	minHeight = 1;
	maxHeight = 2000;
	isPrint = true;
}

class Annotation {
	constructor(task, author, isLock) {
		this.pageIndex = this.pageNumber = task.pageNumber;
		this.author = author;
		this.annotationSettings = new AnnotationSettings(isLock);
		this.isLocked = isLock;
		this.annotationId = nanoid();
		this.customData = new CompletedTaskCustomData;
	}
	isPrint = true;
	opacity = 1;
	// M/d/yyyy h:mm:ss tt
	modifiedDate = new Date().toLocaleString(`en-US`).split(`,`).join(``);
	note = ``;
	review = {
		state: ``
	};
}

export class FreeText extends Annotation {
	constructor(task, author, text, fontFamily, fontSize) {
		super(task, author, true);
		if (text) {
			this.dynamicText = text;
		} else {
			this.dynamicText = customTextContent;
			this.isReadonly = false;
			this.fontColor = customTextFontColor;
		}
		if (fontFamily) {
			this.fontFamily = fontFamily;
			this.customData.fontFamily = fontFamily;
		}
		if (fontSize) {
			this.fontSize = fontSize;
		}
		const measure = textMeasure(
			this.dynamicText,
			this.fontSize,
			this.fontFamily
		);
		const { left, top } = { ...task.bounds };
		this.bounds = {
			left, top, x: left, y: top, ...measure
		};
	}
	subject = `Text Box`;
	rotateAngle = 0;
	strokeColor = `#ffffff00`;
	thickness = 1;
	fillColor = `#ffffff00`;
	shapeAnnotationType = AnnotationType.FreeText;
	fontColor = textFontColor;
	fontSize = defaultFontSize;
	fontFamily = fontFamily;
	textAlign = TextAlign.Left;
	font = {
		"isBold": false,
		"isItalic": false,
		"isStrikeout": false,
		"isUnderline": false
	};
	annotationSelectorSettings = {
		"selectionBorderColor": selectColor,
		"resizerBorderColor": selectColor,
		"resizerFillColor": selectColor,
		"resizerSize": 8,
		"selectionBorderThickness": 1,
		resizerShape,
		selectorLineDashArray,
		resizerLocation,
		"resizerCursorType": null
	};
	allowedInteractions = textAllowedInteractions;
	isReadonly = true;
}

export class BlackLine extends Annotation {
	constructor(task, author) {
		super(task, author, false);
		const { height, left, top, width } = { ...task.bounds };
		const y = Math.floor(top + height / 2);
		const right = left + width;
		this.bounds = {
			left,
			top: y,
			height: 0,
			width,
			right,
			bottom: y
		};
		this.vertexPoints = [{ x: left, y }, { x: right, y }];
	}
	shapeAnnotationType = AnnotationType.BlackLine;
	subject = AnnotationType.BlackLine;
	strokeColor = `#000000`;
	fillColor = `#ffffff00`;
	thickness = 2;
	borderStyle = ``;
	borderDashArray = 0;
	rotateAngle = `RotateAngle0`;
	isCloudShape = false;
	cloudIntensity = 0;
	lineHeadStart = `None`;
	lineHeadEnd = `None`;
	rectangleDifference = [];
	annotationSelectorSettings = {
		"selectionBorderColor": ``,
		"resizerBorderColor": selectColor,
		"resizerFillColor": selectColor,
		"resizerSize": 8,
		"selectionBorderThickness": 1,
		resizerShape,
		"selectorLineDashArray": [],
		resizerLocation,
		"resizerCursorType": null
	};
	isCommentLock = false;
	enableShapeLabel = false;
}

export const checkmarkData = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADEAAAAkCAYAAAAgh9I0AAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAADzSURBVHgB7ZnLDcIwEAWnBEp4JVACnUAJlLAdQAeUQimUQAngiETik5iA4mxW8khPudi7HiV2DoZ4KOWccmufK4KhlAsPgS5HAiE+Bbq3EQLRL9BkRwDEsIARAFEFfBFVwBdRBXwRVcAXUQV8EVWgn33KoW1QElFI4PRU6Jqypgyi4Cf0XrCEiCi8B86UFREzbGINNJlCRMx4ConpRYTDMSqmExGO/wFlmm8nqGHMhPhfJDfXmBnxu0hujuGEGC+SG2s4I76L5MYYC0HkF7l4gQ4xvNgQAh1inIixcERexAiC6BcxgiFeRYygNNdMGxZw3XQH6XEps9kHEtkAAAAASUVORK5CYII=`;
export const circleData = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACwAAAAsCAYAAAAehFoBAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAJGSURBVHgB1ZmNecIgEIY/u0AzAhvoBqUbOIIb6AZmA7uBbmA7QdIJtBPETqAbWM6EGg+Ihvzh+zzXBkzgCxxHgBGaESmTyt6UCWWTIi8qfj8pOxT/98q+laVFulekskTZUdnZw7bKpugBqiSDn0ibUVkzdIBA3qLnjmxX1NEKc1R3PbXSB/KWErj6L4pr8utpcU9WUQ7VsUBDlhUVrJH7cl3oBTYV5S7hiUtsgna6T8AtvLZom9hWuszCAnaXe1j0HHY/naA7BOz+fbeB6EH+thlaHMF36uaij7hT967uAy0zgdlgievmGTy6pAMWFh3WWZF3xxrDkcB0yxumMN9KYDgETD2yfMMW4bSuZgOHL0cw36bLEPYoEmYAuEz73B0yhAMfV9MXmN8DXwgHrkWS4DHLTBEOe5YWJDhimQeEQ8rSl8blM0uEcOAB4TgqLsqMEBY3+l7wZJBgvuQOzSXKnGyCXxEOgqUPJJiHjneEg2DpSwv/sswQpmWNZOkf+vNUUzNlkmPzWCwxPNTT/KPsfxAmCO/zkjSUNX2Wf5QI/wPeWCZxf0kwHLx1rePKtkwaYhFq2xeZuW7mvkyDsc8wJ2AGgF3dBzIEvJFC2PYFuhY9gedWlSa2PNzVZqBr/zlGTWJLITpGCzRHwL2rH8OT2FGgFu4zICXMsNWKWI1r/7bs3yvkYVEfe2noWiAPSyv0cGSgETB3Ntu0BB0N6hnaP/bq7bxuCz+R1PXUohIeNF0h66NbsjGuPls+utXHtrRQSNHw6PYPT8u4MLE70E8AAAAASUVORK5CYII=`;
export const crossData = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACwAAAAsCAYAAAAehFoBAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAEjSURBVHgB1dnRboQgFEXR3flxz3y57SSetjHoIAL3zk7wAXhYMZHEAH8t28hW0fWaWLeRCV10/Z/MhC66Hj+Pr8JmEYteNsO+X+trcSXHm14OLNpvFPHoaqwTcejLWCfmo5uxTsxD38Y6MR7dDevEOHR3rBP90cOwTvRDD8c6cR89DetEO3o61onr6DCsE/XocKwT79FpsE4co9NhnSjDUmKd+CCsExOwD/q1Nq6FdPSB1Z7TU6vBpkGfHV0iGfoM60QSdA3WiWD0FawTQegWrBOT0XewTkxC98A6MRjdE+vEIPQIrBOd0SOxTnRCz8A6cRM9E+tEIzoC68RFdCTWiUp0BqwTb9CZsE4coI9ukZ7Eg5+F+eItksiTOHGJhL/i7FzfJqjBWvSeSwAAAAAASUVORK5CYII=`;

function topLeft(bounds, width, height) {
	let left = bounds.left + Math.floor((bounds.width - width) / 2);
	let top = bounds.top + Math.floor((bounds.height - height) / 2);
	return { top, left, x: left, y: top };
}

export class Checkmark extends Annotation {
	static async build(task, author) {
		return new Checkmark(task, author, await ImageWithDimensions(checkmarkData));
	}
	constructor(task, author, image) {
		super(task, author, false);
		const { data, width, height } = { ...image };
		this.stampAnnotationPath = data;
		const { left, top } = topLeft(task.bounds, width, height);
		this.bounds = { left, top, width, height };
	}
	stampAnnotationType = AnnotationType.Image.toLowerCase();
	isDynamicStamp = false;
	shapeAnnotationType = AnnotationType.Stamp;
}	

export class CircleTool extends Annotation {
	static async build(task, author) {
		return new CircleTool(task, author, await ImageWithDimensions(circleData));
	}
	constructor(task, author, image) {
		super(task, author, false);
		const { data, width, height } = { ...image };
		this.stampAnnotationPath = data;
		const { left, top } = topLeft(task.bounds, width, height);
		this.bounds = { left, top, width, height };
	}
	stampAnnotationType = AnnotationType.Image.toLowerCase();
	isDynamicStamp = false;
	shapeAnnotationType = AnnotationType.Stamp;
}

export class Cross extends Annotation {
	static async build(task, author) {
		return new Cross(task, author, await ImageWithDimensions(crossData));
	}
	constructor(task, author, image) {
		super(task, author, false);
		const { data, width, height } = { ...image };
		this.stampAnnotationPath = data;
		const { left, top } = topLeft(task.bounds, width, height);
		this.bounds = { left, top, width, height };
	}
	stampAnnotationType = AnnotationType.Image.toLowerCase();
	isDynamicStamp = false;
	shapeAnnotationType = AnnotationType.Stamp;
}

export class Ink extends Annotation {
	constructor(task, author, pathData, bounds) {
		super(task, author, false);
		this.data = pathData;
		const { width, height } = bounds;
		const { x, y } = topLeft(task.bounds, width, height);
		this.bounds = { x, y, width, height };
	}
	shapeAnnotationType = AnnotationType.Ink;
	strokeColor = `#000000`;
	thickness = 1;
	subject = AnnotationType.Ink;
}

export class SignatureText extends FreeText {
	constructor(task, author, text, fontFamily, fontSize) {
		const textWithPadding = ` ${text} ${fontFamily == Pinyon ? ` ` : ``}`;
		super(task, author, textWithPadding, fontFamily, fontSize ?? defaultSignatureFontSize);
	}
}

const Pinyon = `Pinyon Script`;

export const signatureFontFamilies = [`Dancing Script`, `Homemade Apple`, `Pacifico`, Pinyon];

export function clearSelection(pdfViewer) {
	pdfViewer.select([]);
}

export function clearActivePage(pdfViewer) {
	const viewerBase = pdfViewer.viewerBase;
	viewerBase.activeElements.activePageID = NaN;
	viewerBase.annotationEvent = null;
}

export const Authors = Object.freeze({
	Notary: `Notary`,
	Client: `Client`,
});

export function isImageToolActive(pdfViewer) {
	const base = pdfViewer.viewerBase;
	return base.stampAdded && !base.isAlreadyAdded;
}

export function isImagePreviewActive(pdfViewer) {
	const base = pdfViewer.viewerBase;
	return base.tool?.inAction ?? false;
}